import { BellIcon, CheckCheckIcon } from "lucide-react"
import type * as React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { FeatureOff } from "@/components/FeatureOff"
import { Button } from "@/components/ui/button"
import {
  NotificationList,
  useDismissedNotifications,
} from "@/features/notifications/NotificationList"
import {
  isModuleOff,
  useMarkAllNotificationsRead,
  useNotifications,
} from "@/features/notifications/use-notifications"

/**
 * The hub — one person's inbox, at its own URL (spec §17.7, §18.3).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE BELL OPENS A DRAWER NOW; THIS ROUTE STAYS, AND THAT IS DELIBERATE.
 *
 * A drawer is the right shape for "what is waiting for me" — it is a glance, it
 * keeps the reader on the screen they were working on, and it costs no
 * navigation. What it cannot be is a DESTINATION: `/notifications` is already in
 * links sent over WhatsApp, in browser history, and it is where a notification
 * email will point (§17A). Deleting the route to move the list into a panel
 * would break every one of those with a redirect to Home, which reads as the
 * link having been wrong.
 *
 * SO BOTH SURFACES RENDER `NotificationList` — literally the same component,
 * not the same design. See that file: two implementations is how a drawer ends
 * up marking read on open while the page does not.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * WHAT THIS SCREEN IS NOT. It is not a messenger. The shared conversation lives
 * in `features/messages`, with shared read semantics; mixing the two would put
 * "the CA has read it, the owner has not" into a table that cannot express it.
 * Nothing here composes, replies to, or addresses anything.
 */
export function NotificationsScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const [unreadOnly, setUnreadOnly] = useState(false)
  const query = useNotifications({ unreadOnly })
  const markAll = useMarkAllNotificationsRead()
  const dismissed = useDismissedNotifications()

  /*
   * §18.3 — THE 404 IS THE FLAG. There is no `useFlag` on the client (see
   * `use-notifications.ts`), and a disabled feature must read as "not switched
   * on" rather than as a failure the reader could retry their way out of.
   */
  if (query.isError && isModuleOff(query.error)) {
    /*
     * ONE COMPONENT AND ONE PAIR OF STRINGS FOR EVERY FLAGGED MODULE —
     * `components/FeatureOff.tsx`. The hub used to carry its own
     * `notif.offTitle`/`offHelp`; the module name comes from this screen's own
     * title because the hub deliberately has no sidebar row.
     */
    return (
      <div className="flex flex-col gap-4 p-4 md:p-6">
        <FeatureOff module={t("notif.title")} icon={<BellIcon />} />
      </div>
    )
  }

  const unread = query.data?.unread ?? 0

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t("notif.title")}</h1>
          <p className="text-muted-foreground text-sm">
            {unread > 0 ? t("notif.unreadCount", { count: unread }) : t("notif.allCaughtUp")}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/*
            * TWO BUTTONS RATHER THAN A DROPDOWN. There are exactly two answers
            * and both fit on the narrowest phone; a select that has to be opened
            * to discover it holds two options costs a tap to learn nothing.
            */}
          <Button
            variant={unreadOnly ? "outline" : "secondary"}
            onClick={() => {
              setUnreadOnly(false)
            }}
          >
            {t("notif.filterAll")}
          </Button>
          <Button
            variant={unreadOnly ? "secondary" : "outline"}
            onClick={() => {
              setUnreadOnly(true)
            }}
          >
            {t("notif.filterUnread")}
          </Button>

          {/*
            * "Mark all read" IS NOT A DELETE, and the copy must not imply one.
            * The rows stay with their `read_at` so "was anybody ever told this
            * bill needed an e-way bill" still has an answer months later — which
            * is the reason the table exists at all. Hidden at zero: a control
            * that does nothing is one the reader has to press to find that out.
            *
            * IT DOES NOT HIDE ANYTHING HERE, unlike the drawer's "Clear all".
            * This screen is where somebody comes to LOOK — hiding rows on the
            * page you opened to read them would be perverse.
            */}
          {unread > 0 ? (
            <Button
              variant="outline"
              disabled={markAll.isPending}
              onClick={() => {
                markAll.mutate()
              }}
            >
              <CheckCheckIcon />
              {t("notif.markAllRead")}
            </Button>
          ) : null}
        </div>
      </header>

      <NotificationList unreadOnly={unreadOnly} dismissed={dismissed} />
    </div>
  )
}
